import React, { useEffect, Suspense, useState } from "react";
import { useParams } from "react-router-dom";

import { IconRefresh } from '@tabler/icons-react';

import { useObjectStore } from "../zustand/objects";
import { SubscribeReminderBar } from "../components/AppNavBar";
import StoryboardTextField from "../components/StoryboardTextField";
import { Screenshot } from "../components/Screenshot";
import {
  CopyPreviewLink,
  CopyStoryboardEmbed,
  CopyPreviewEmbed,
  CopyStoryboardTable,
} from "../components/CopyStoryboardTable";
import { ExportToAPIButton } from "../components/TopProjectAppBar.js";
import TopProjectAppBar from "../components/TopProjectAppBar";

import {
  AppShell,
  Box,
  Card,
  Button,
  Modal,
  LoadingOverlay,
  Group,
  Text,
  Stack,
  Container,
  Title,
  SimpleGrid,
  Tooltip,
} from "@mantine/core";

import StoryboardToolbarIcon from "../components/StoryboardToolbarIcon";
import { LazyStoryboardSceneThumb } from "../components/StoryboardSceneThumbPreview";

import {
  editorModalShell,
  editorModalStrongTitleStyles,
  editorModalSecondaryButtonStyles,
  getStoryboardChromeTheme,
  storyboardCardMutedContentSectionStyle,
  storyboardCardRootStyleFromTheme,
  storyboardCardThumbStyleFromTheme,
  storyboardSceneButtonStyles,
  pageLayoutProps,
} from "../styleFile";
import { CHROME_HEADER_HEIGHT } from "../utils/uiChromeTokens";

export default function EditorStoryboardPage() {
  const {
    objectsAreLoaded,
    updateArtboardAndPreviewCamera,
    unloadObjects,
    loadObjects,
    artboards,
    getSpecificUserProject,
    getAIModels,
  } = useObjectStore();
  const viewportThemeOverride = useObjectStore((s) => s.viewportThemeOverride);
  const isDarkChrome = viewportThemeOverride === "dark";
  const sbTheme = getStoryboardChromeTheme(isDarkChrome);
  const { projectKey } = useParams();

  const result = [];
  const [snapping, setSnapping] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.resolve(unloadObjects());
      if (cancelled) return;
      await Promise.resolve(getSpecificUserProject(projectKey));
      if (cancelled) return;
      await Promise.resolve(getAIModels());
      if (cancelled) return;
      await Promise.resolve(loadObjects());
      if (cancelled) return;
      await Promise.resolve(updateArtboardAndPreviewCamera(1));
    })();
    return () => {
      cancelled = true;
    };
  }, [getAIModels, getSpecificUserProject, loadObjects, projectKey, unloadObjects, updateArtboardAndPreviewCamera]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.setAttribute("data-editor-theme", isDarkChrome ? "dark" : "light");
    return () => {
      document.body.removeAttribute("data-editor-theme");
    };
  }, [isDarkChrome]);

  const [artboardNumber, setArtboardNumber] = useState("1");

  function runIteration(i = 1) {
    const totalArtboards = Object.keys(artboards).length;
    if (i > totalArtboards) {
      return;
    }
    setArtboardNumber(i);
    setTimeout(() => {
      runIteration(i + 1);
    }, 1000);
  }

  const removeGroup = () => {
    setSnapping(false);
  };

  const updateAllScreenshots = () => {
    Promise.resolve(unloadObjects())
      .then(() => getSpecificUserProject(projectKey))
      .then(() => getAIModels())
      .then(() => loadObjects())
      .then(() => updateArtboardAndPreviewCamera(1))
      .then(() => setArtboardNumber("1"))
      .then(() => setSnapping(true))
      .then(() =>
        setTimeout(() => {
          runIteration();
        }, 1000)
      );
  };

  const totalScenes = artboards ? Object.keys(artboards).length : 0;

  return (
    <>
      <Stack gap={0} style={{ minHeight: "100vh", width: "100%", backgroundColor: sbTheme.stackBg }}>
        <SubscribeReminderBar />
        <AppShell header={{ height: CHROME_HEADER_HEIGHT }} padding={0}>
          <AppShell.Header style={{ padding: 0, backgroundColor: sbTheme.headerBg }}>
            <TopProjectAppBar mode="storyboard" projectKey={projectKey} />
          </AppShell.Header>

          <AppShell.Main
            p={0}
            style={{
              backgroundColor: sbTheme.mainBg,
              minHeight: `calc(100vh - ${CHROME_HEADER_HEIGHT}px)`,
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              overflowY: "auto",
              paddingBottom: 24,
            }}
          >
            <LoadingOverlay visible={snapping} zIndex={500} />

            {artboards && (
              <Modal
                opened={snapping}
                onClose={() => {}}
                centered
                closeOnEscape={false}
                closeOnClickOutside={false}
                withCloseButton={false}
                size="lg"
                radius={editorModalShell.radius}
                overlayProps={editorModalShell.overlayProps}
                styles={editorModalStrongTitleStyles}
                title="Capturing screenshots"
              >
                <Stack gap="md" p="md" pt={0}>
                  <Box style={{ maxWidth: "100%", overflow: "hidden" }}>
                    <Screenshot
                      page="storyboard"
                      result={result}
                      removeGroup={removeGroup}
                      index={artboardNumber}
                      projectKey={projectKey}
                      guestMode={false}
                      artboard={artboardNumber}
                    />
                  </Box>
                  <Text size="sm" fw={500} c={sbTheme.mutedTextColor}>
                    Scene {artboardNumber} of {totalScenes}
                  </Text>
                </Stack>
              </Modal>
            )}

            <Container {...pageLayoutProps}>
              <Stack gap="lg">
                <Stack gap="md">
                  <Box>
                    <Text
                      style={{
                        ...editorModalShell.styles.title,
                        color: sbTheme.sectionLabelColor,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Storyboard
                    </Text>
                    <Title
                      order={2}
                      fw={600}
                      mt={4}
                      style={{
                        color: sbTheme.headlineColor,
                        fontSize: "1.125rem",
                        lineHeight: 1.35,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      Edit or share your storyboard
                    </Title>
                  </Box>

                  <Group justify="space-between" align="center" wrap="wrap" gap="md" style={{ rowGap: 5 }}>
                    <Group gap={8} wrap="wrap">
                      <CopyStoryboardEmbed projectKey={projectKey} storyboardToolbar />
                      <CopyPreviewEmbed projectKey={projectKey} storyboardToolbar />
                      {artboards && (
                        <CopyStoryboardTable projectKey={projectKey} storyboardToolbar />
                      )}
                      <CopyPreviewLink projectKey={projectKey} storyboardToolbar />
                      <StoryboardToolbarIcon label="Refresh screenshots" onClick={() => updateAllScreenshots()}>
                        <IconRefresh size={20} />
                      </StoryboardToolbarIcon>
                      <ExportToAPIButton projectKey={projectKey} storyboardToolbar />
                    </Group>
                  </Group>
                </Stack>

                <Suspense
                  fallback={<img src="/image/loading.gif" alt="" style={{ width: "50px", height: "50px" }} />}
                >
                  <SimpleGrid cols={{ base: 2, md: 3, lg: 4, xl: 5 }} spacing="sm" verticalSpacing="sm">
                    {objectsAreLoaded &&
                      artboards &&
                      Object.keys(artboards).map((mapped) => (
                        <Card
                          key={mapped}
                          p={0}
                          radius={editorModalShell.radius}
                          withBorder={false}
                          shadow="sm"
                          styles={{
                            root: {
                              ...storyboardCardRootStyleFromTheme(sbTheme),
                              "&:hover": {
                                boxShadow: sbTheme.hoverShadow,
                              },
                            },
                          }}
                        >
                          <Card.Section p={0}>
                            <Box
                              style={{
                                position: "relative",
                                ...storyboardCardThumbStyleFromTheme(sbTheme),
                                backgroundImage: sbTheme.thumbPlaceholderGradient,
                              }}
                            >
                              <LazyStoryboardSceneThumb
                                sceneKey={mapped}
                                previewKey={`${projectKey}-${mapped}`}
                                objectsAreLoaded={objectsAreLoaded}
                                artboards={artboards}
                              />
                            </Box>
                          </Card.Section>
                          <Box style={storyboardCardMutedContentSectionStyle(sbTheme)}>
                            <Stack gap="sm" px="md" pt="md" pb={6}>
                              <Text
                                fw={600}
                                fz={13}
                                c={sbTheme.titleColor}
                                style={{
                                  lineHeight: 1.3,
                                }}
                              >
                                Scene: {mapped}
                              </Text>
                              <StoryboardTextField fieldType="name" isEditable artboard={mapped} compact />
                              <StoryboardTextField fieldType="description" isEditable artboard={mapped} compact />
                              <Suspense fallback={null}>
                                <Tooltip label="This is edit mode. Comments are written in preview mode." position="bottom-start">
                                  <span>
                                    <StoryboardTextField fieldType="comments" isEditable={false} artboard={mapped} compact />
                                  </span>
                                </Tooltip>
                              </Suspense>
                            </Stack>
                            <Group gap="sm" grow px="md" pb="md" pt={4}>
                              <Button
                                styles={storyboardSceneButtonStyles}
                                component="a"
                                href={`/preview/${projectKey}?artboard=${mapped}`}
                                onClick={() => unloadObjects()}
                              >
                                View
                              </Button>
                              <Button
                                styles={editorModalSecondaryButtonStyles}
                                style={{ height: 32, minHeight: 32 }}
                                component="a"
                                href={`/editor/${projectKey}?artboard=${mapped}`}
                              >
                                Edit
                              </Button>
                            </Group>
                          </Box>
                        </Card>
                      ))}
                  </SimpleGrid>
                </Suspense>
              </Stack>
            </Container>
          </AppShell.Main>
        </AppShell>
      </Stack>
    </>
  );
}
